import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Notifications } from "./components/partials/Notifications";
import CategoryPosts from "./components/Category/CategoryPosts";
import PostDetails from "./components/PostDetails/PostDetails";
import { Route, Routes, useLocation } from "react-router-dom";
import ReactionsBar from "./components/partials/ReactionBar";
import { Settings } from "./components/Settings/Settings";
import SidebarProvider from "./contexts/SidebarContext";
import { Profile } from "./components/Profile/Profile";
import SearchProvider from "./contexts/SearchContext";
import Header from "./components/partials/Header";
import Signup from "./components/Auth/Signup";
// import Login from "./components/Auth/Login";
import AppLayout from "./layouts/AppLayout";
import Home from "./components/Home/Home";
import { Toaster } from "react-hot-toast";
import React, { useEffect } from "react";
import Starter from "./components/Starter";
import News from "./components/News/News";
import NotFound from "./components/NotFound";

import "./App.css";

function App() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const loggedInCheck = queryParams.get("logged_in_check");
  const queryClient = new QueryClient();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/session/current.json`,
          {
            method: "GET",
            credentials: "include", // Include cookies in the request
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const { current_user, api_key } = data;
        current_user.api_key = api_key;
        return current_user;
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchUserInformation = async (username, user_id, api_key) => {
      try {
        const encodedUsername = encodeURIComponent(username); // Encode for URL
        // const base64Username = btoa(
        //     unescape(encodeURIComponent(username))
        // ); // Base64 encode username

        const headers = new Headers();
        headers.append("Api-Key", api_key);
        headers.append("Api-Username", user_id);
        headers.append("Content-Type", "application/json");

        const url = `${process.env.REACT_APP_API_URL}/u/${encodedUsername}.json`;
        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const { user } = data;
        user.api_key = api_key;
        localStorage.setItem("salla_discourse_user", JSON.stringify(user));
        if (loggedInCheck) {
          window.location.href = process.env.REACT_APP_URL;
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchSallaUserData = async () => {
      const current_user = await fetchUserData();
      await fetchUserInformation(
        current_user?.username,
        current_user?.id,
        current_user.api_key
      );
    };

    if (loggedInCheck) {
      fetchSallaUserData();
    }
  }, [loggedInCheck]);

  const showHeader =
    location.pathname !== "/login" && location.pathname !== "/signup";

  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
        }}
      />
      <SearchProvider>
        <QueryClientProvider client={queryClient}>
          <SidebarProvider>
            {showHeader && <Header />}
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Home />} />
                <Route
                  path="/detail/:topicId/:postNumber?"
                  element={<PostDetails />}
                />
                <Route
                  path="/category-detail/:categoryId"
                  element={<CategoryPosts />}
                />
                <Route path="/profile" element={<Profile />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/starter" element={<Starter />} />
                <Route path="/news" element={<News />} />
                <Route path="?create-post=true" element={null} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/signup" element={<Signup />} />
              <Route path="/reactions" element={<ReactionsBar />} />
              {/* <Route path="/login" element={<Login />} /> */}
            </Routes>
          </SidebarProvider>
        </QueryClientProvider>
      </SearchProvider>
    </div>
  );
}

export default App;
